/**
 * Popup coordinator.
 *
 * WHY THIS EXISTS: the three interruptive surfaces (cookie consent, email
 * capture, local-service-area) each had their own independent timer — 1s, 10s
 * and 5s — and no knowledge of one another. On a first visit all three appeared
 * and stayed, stacked on top of the hero, leaving the page unusable until the
 * visitor dismissed each one. Blinds.com, by comparison, shows exactly one
 * interruption with a "No thanks, maybe later" opt-out.
 *
 * Rules enforced here:
 *  1. Cookie consent is legally required and always goes FIRST, alone.
 *  2. Nothing promotional appears until consent has been answered.
 *  3. At most ONE promotional popup per page view — first to claim wins.
 *
 * Deliberately module-scoped (not React state): these components are siblings
 * with no shared parent, and a context provider would force the whole tree to
 * re-render on every popup change.
 */

const CONSENT_KEY = 'cookie-consent';

/** Has the visitor answered the cookie banner yet? */
export function consentSettled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(CONSENT_KEY) !== null;
  } catch {
    // Private mode / blocked storage — don't trap the user behind a banner
    // that can never be recorded as dismissed.
    return true;
  }
}

// Which promotional popup currently owns the screen, if any.
let activePromo: string | null = null;

const listeners = new Set<() => void>();

/** Notify subscribers that the slot changed (e.g. a popup closed). */
function emit() {
  listeners.forEach((l) => {
    try { l(); } catch { /* a broken listener must not block the others */ }
  });
}

/**
 * Try to take the single promotional slot.
 * Returns true if this popup may show. Idempotent for the same owner, so a
 * re-render can't cause a component to lose a slot it already holds.
 */
export function claimPromoSlot(owner: string): boolean {
  if (activePromo === null || activePromo === owner) {
    activePromo = owner;
    return true;
  }
  return false;
}

/** Release the slot on close so a later popup can use it this session. */
export function releasePromoSlot(owner: string): void {
  if (activePromo === owner) {
    activePromo = null;
    emit();
  }
}

/** True when no promotional popup is on screen. */
export function promoSlotFree(): boolean {
  return activePromo === null;
}

/** Subscribe to slot changes; returns an unsubscribe function. */
export function onPromoSlotChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
